import { AlertTriangle, Ban, CheckCircle2, CircleDot, Siren } from 'lucide-react';
import { cn } from '@/lib/utils';

// Sprint 6.9.1.2 — badge réutilisable pour statuts paiements consolidés.
// Sprint 6.9.2 — généralisé : ajout variant 'partiel' pour les dettes fournisseurs.
//
// Le statut "en_retard" est CALCULÉ à partir de (statut='a_payer' && date_référence < aujourd'hui - 30j).
// Pas stocké en DB — passé via prop `enRetard`.

export type ConsolideStatut = 'a_payer' | 'paye' | 'non_retribue' | 'partiel';

export interface StatutPaiementBadgeProps {
  statut: ConsolideStatut;
  enRetard?: boolean;
  size?: 'sm' | 'md';
}

const TONES = {
  paye: {
    bg: 'bg-emerald-100',
    text: 'text-emerald-700',
    ring: 'ring-emerald-200',
    Icon: CheckCircle2,
    label: 'Payé',
  },
  a_payer: {
    bg: 'bg-amber-100',
    text: 'text-amber-700',
    ring: 'ring-amber-200',
    Icon: AlertTriangle,
    label: 'Non payé',
  },
  non_retribue: {
    bg: 'bg-slate-100',
    text: 'text-slate-600',
    ring: 'ring-slate-200',
    Icon: Ban,
    label: 'Non rétribué',
  },
  partiel: {
    bg: 'bg-amber-100',
    text: 'text-amber-800',
    ring: 'ring-amber-200',
    Icon: CircleDot,
    label: 'Partiel',
  },
  en_retard: {
    bg: 'bg-rose-100',
    text: 'text-rose-700',
    ring: 'ring-rose-200',
    Icon: Siren,
    label: 'En retard',
  },
} as const;

export function StatutPaiementBadge({
  statut,
  enRetard,
  size = 'md',
}: StatutPaiementBadgeProps): JSX.Element {
  const tone = enRetard && statut === 'a_payer' ? TONES.en_retard : TONES[statut];
  const sizeClass = size === 'sm' ? 'px-2 py-0.5 text-[10px] gap-1' : 'px-2.5 py-1 text-xs gap-1.5';
  const iconSize = size === 'sm' ? 'h-2.5 w-2.5' : 'h-3 w-3';
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full font-semibold uppercase tracking-wider ring-1',
        tone.bg,
        tone.text,
        tone.ring,
        sizeClass,
      )}
    >
      <tone.Icon className={cn(iconSize, 'shrink-0')} aria-hidden />
      {tone.label}
    </span>
  );
}

/**
 * Helper : applique la logique "en retard" sur les dates.
 * Un paiement/dette est en retard si statut='a_payer' ET date_référence < (aujourd'hui - 30j).
 */
export function isPaiementEnRetard(statut: ConsolideStatut, dateReference: string | null): boolean {
  if (statut !== 'a_payer' || !dateReference) return false;
  const d = new Date(dateReference);
  if (isNaN(d.getTime())) return false;
  const seuil = Date.now() - 30 * 24 * 3600 * 1000;
  return d.getTime() < seuil;
}
